import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import {
  selectBudgetApprover,
  selectBudgetRecipient,
  selectBudgetRequest,
  selectCreditCardIdFromRoute,
  selectCreditCardsFullPaginatedData,
  selectErrorDetailMapFromState,
  selectFilteredCreditCardOptions,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectPuttingCreditCardFromState,
  selectTopLevelErrorFromState,
  selectUsersFullPaginatedData,
} from 'src/app/UpdateBudgetRequest/redux/updateBudgetRequest.selectors'
import { BudgetFlatFormValues } from 'src/components/BudgetForm/BudgetForm'
import BudgetPickContent from 'src/components/BudgetPickContent/BudgetPickContent'
import { CreditCardDropdownInfiniteScrollProps } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import UpdateBudgetRequestRecurringContent from 'src/components/UpdateBudgetRequestRecurringContent/UpdateBudgetRequestRecurringContent'
import UpdateBudgetRequestStandardContent from 'src/components/UpdateBudgetRequestStandardContent/UpdateBudgetRequestStandardContent'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useFlyoutDispatch } from 'src/hooks'
import { BudgetRequestStatus } from 'src/models/budgetRequest.models'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import { getBudgetRequest } from 'src/redux/actions/data/budgetRequests.actions'
import {
  getCreditCards,
  putBudgetRequestAndOpenToastAndCloseFlyout,
} from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { selectAllStatisticsLoadedFromState } from 'src/redux/selectors/app.selectors'
import {
  selectIsConfirmBudgetModalUpdateBudgetDismissed,
  selectIsUserOrglessRCNUser,
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { PutCreditCardRequest } from 'src/requests/putCreditCard.request'

const UpdateBudgetRequest: React.FC = () => {
  const id = useSelector(selectCreditCardIdFromRoute)
  const budgetRequest = useSelector(selectBudgetRequest)
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const recipient = useSelector(selectBudgetRecipient)
  const approver = useSelector(selectBudgetApprover)
  const userId = useSelector(selectUserIdFromState)
  const users = useSelector(selectUserListModelsFromState)
  const puttingBudgetRequest = useSelector(selectPuttingCreditCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const isUserOrglessRCNUser = useSelector(selectIsUserOrglessRCNUser)
  const isConfirmBudgetModalDismissed = useSelector(
    selectIsConfirmBudgetModalUpdateBudgetDismissed,
  )
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const filteredRcnOptions = useSelector(selectFilteredCreditCardOptions)

  const loggedInUser = userId !== undefined ? users[userId] : undefined
  const approverMe = userId === budgetRequest?.approver.id
  const showCurrencyError =
    approverMe && budgetRequest?.currency !== loggedInUser?.currency
  const dispatch = useFlyoutDispatch()

  useEffect(() => {
    dispatch(getBudgetRequest(id))
  }, [id])

  const putBudgetRequest = (
    body: Partial<PutCreditCardRequest>,
    status: BudgetRequestStatus,
  ): void => {
    dispatch(
      putBudgetRequestAndOpenToastAndCloseFlyout({ ...body }, id, status),
    )
  }

  useEffect(() => {
    if (
      statisticsLoaded.creditCardsSourceCountStatisticsLoaded &&
      isUserOrglessRCNUser &&
      !isConfirmBudgetModalDismissed
    ) {
      dispatch(createSetIsSetupOrganizationPopupOpen(true))
    }
  }, [
    isUserOrglessRCNUser,
    statisticsLoaded.creditCardsSourceCountStatisticsLoaded,
  ])

  const currentScreen = budgetRequest?.recurs
    ? BudgetScreen.Recurring
    : BudgetScreen.Standard

  const [screen, setScreen] = useState(currentScreen)

  useEffect((): void => {
    setScreen(currentScreen)
  }, [currentScreen])

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const fetchCreditCards = (filterInput?: string, page?: number): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        sortField: 'type',
        sortDirection: 'ASC',
        type: CreditCardType.Source,
        withPermission: CreditCardPermissions.IssueDelegateCreditCards,
        budgetsEnabled: true,
      }),
    )
  }
  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: fetchCreditCards,
    ...creditCardsFullPaginatedData,
  }

  const initialFlatFields = useMemo(
    (): BudgetFlatFormValues => ({
      displayName: budgetRequest?.displayName,
      email: recipient?.email,
      emails: recipient?.email ? [recipient.email] : [],
      issuingLimitDollars: budgetRequest?.issuingLimit,
      untilMoment: budgetRequest?.untilTimezonedMoment,
      isUnlimited: budgetRequest?.issuingLimitCents === undefined,
      isForever: budgetRequest?.untilTimezonedMoment === undefined,
      currency: budgetRequest?.currency,
      notes: budgetRequest?.notes,
    }),
    [budgetRequest, recipient],
  )

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {budgetRequest !== undefined &&
      recipient !== undefined &&
      approver !== undefined &&
      userId ? (
        <Fragment>
          {screen === BudgetScreen.Pick && (
            <BudgetPickContent setScreen={setScreen} />
          )}
          {screen === BudgetScreen.Standard && (
            <UpdateBudgetRequestStandardContent
              recipient={recipient}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              rcnOptions={filteredRcnOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              puttingBudgetRequest={puttingBudgetRequest}
              putBudgetRequest={putBudgetRequest}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userInfiniteScrollProps={userInfiniteScrollProps}
              initialFlatFields={initialFlatFields}
              approving={approver?.id === userId}
              requesting={recipient?.id === userId}
              isFormDisabled={false}
              approver={approver}
              showCurrencyError={showCurrencyError}
            />
          )}
          {screen === BudgetScreen.Recurring && (
            <UpdateBudgetRequestRecurringContent
              budgetRequest={budgetRequest}
              recipient={recipient}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              rcnOptions={filteredRcnOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              puttingBudgetRequest={puttingBudgetRequest}
              putBudgetRequest={putBudgetRequest}
              setScreen={setScreen}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              isFormDisabled={false}
              initialFlatFields={initialFlatFields}
              userInfiniteScrollProps={userInfiniteScrollProps}
              requesting={recipient?.id === userId}
              approving={approver?.id === userId}
              approver={approver}
              showCurrencyError={showCurrencyError}
            />
          )}
        </Fragment>
      ) : (
        <NotFoundRightFlyoutContent />
      )}
    </RightFlyout>
  )
}

export default UpdateBudgetRequest
